/**
 * Networking service layer (SSOT for backend HTTP calls).
 *
 * Mirrors the layered convention used by `mentorshipService.ts`:
 * - Local TypeScript types at the top
 * - Functions that throw on error (no union return types)
 * - All HTTP via `apiClient`
 * - `{count, next, previous, results}` envelope unwrap for paginated calls
 *
 * Adding a new endpoint = one type + one function here. Components
 * never call `apiClient` directly so the wire shape stays in one place.
 */

import { apiClient } from '../../apiClient';
import { apiUrl as url } from '../../apiUrls';

// ────────────────────────────────────────────────────────────────────────
// Types — match the backend serializer field lists exactly
// ────────────────────────────────────────────────────────────────────────

export type ConnectionStatus = 'pending' | 'accepted' | 'declined';
export type ProfileVisibility = 'public' | 'connections' | 'private';
export type AbuseCategory = 'spam' | 'harassment' | 'impersonation' | 'inappropriate' | 'other';
export type AbuseState = 'open' | 'reviewing' | 'actioned' | 'dismissed';
export type GroupRole = 'member' | 'admin' | 'owner';
export type ConnectionLabel = 'self' | 'connected' | 'pending_sent' | 'pending_received' | 'none';
export type ConversationKind = 'direct' | 'group';

export type Professional = {
    id: number;
    name: string;
    email: string;
    role: string;
    company: string;
    location: string;
    avatar: string;
    headline: string;
    online: boolean;
    skills: string[];
    connection_status: ConnectionLabel;
};

export type ProfessionalWithMutualCount = Professional & {
    mutual_count: number;
};

export type NetworkProfile = {
    role: string;
    company: string;
    location: string;
    skills: string[];
    avatar: string;
    avatar_url: string;
    bio: string;
    headline: string;
    linkedin_url: string;
    website_url: string;
    visibility: ProfileVisibility;
    is_online: boolean;
    last_seen: string | null;
};

export type Connection = {
    id: string;
    from_user: Professional;
    to_user: Professional;
    status: ConnectionStatus;
    removed_at: string | null;
    created_at: string;
};

export type DirectMessage = {
    id: string;
    sender: Professional;
    receiver: Professional;
    content: string;
    read_at: string | null;
    edited_at: string | null;
    deleted_at: string | null;
    created_at: string;
};

export type GroupMessage = {
    id: string;
    sender: Professional | null;
    content: string;
    edited_at: string | null;
    deleted_at: string | null;
    created_at: string;
};

export type GroupMembership = {
    id: string;
    user: Professional;
    role: GroupRole;
    joined_at: string;
    left_at: string | null;
};

export type GroupChat = {
    id: string;
    name: string;
    description: string;
    avatar: string;
    members: Professional[];
    last_message: string | null;
    last_message_time: string | null;
    unread_count: number;
    created_at: string;
    updated_at: string;
};

export type ConversationSummary = {
    kind: ConversationKind;
    id: string;
    display_name: string;
    avatar_url: string;
    last_message: string;
    last_message_at: string | null;
    unread_count: number;
};

export type Block = {
    id: string;
    blocked: number;
    blocked_user: Professional;
    reason: string;
    created_at: string;
};

export type AbuseReport = {
    id: string;
    reporter: number | null;
    target: number | null;
    target_user: Professional | null;
    category: AbuseCategory;
    details: string;
    state: AbuseState;
    created_at: string;
    reviewed_at: string | null;
};

export type PagedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

// ────────────────────────────────────────────────────────────────────────
// Profile
// ────────────────────────────────────────────────────────────────────────

export async function getMyNetworkProfile(): Promise<NetworkProfile> {
    const r = await apiClient.get(url('/api/networking/profile/'));
    return r.data;
}

export async function updateMyNetworkProfile(
    fields: Partial<Pick<NetworkProfile,
        'role' | 'company' | 'location' | 'skills' | 'avatar' |
        'bio' | 'headline' | 'linkedin_url' | 'website_url' | 'visibility'
    >>,
): Promise<NetworkProfile> {
    const r = await apiClient.put(url('/api/networking/profile/'), fields);
    return r.data;
}

export async function uploadAvatar(file: File): Promise<NetworkProfile> {
    const form = new FormData();
    form.append('file', file);
    const r = await apiClient.post(
        url('/api/networking/profile/avatar/'),
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return r.data;
}

// ────────────────────────────────────────────────────────────────────────
// Professionals / discovery
// ────────────────────────────────────────────────────────────────────────

export async function listProfessionals(
    filters: {
        search?: string;
        filter?: 'online' | 'nearby' | 'mutual';
        skills?: string[];
        location?: string;
        page?: number;
    } = {},
): Promise<PagedResponse<Professional>> {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.filter) params.set('filter', filters.filter);
    if (filters.skills?.length) params.set('skills', filters.skills.join(','));
    if (filters.location) params.set('location', filters.location);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const r = await apiClient.get(url(`/api/networking/professionals/${qs ? '?' + qs : ''}`));
    return r.data;
}

export async function getProfessional(userId: number): Promise<Professional> {
    const r = await apiClient.get(url(`/api/networking/professionals/${userId}/`));
    return r.data;
}

export async function listMutualConnections(
    userId: number,
    page: number = 1,
): Promise<PagedResponse<Professional>> {
    const r = await apiClient.get(
        url(`/api/networking/professionals/${userId}/mutual/?page=${page}`),
    );
    return r.data;
}

export async function getPeopleYouMayKnow(): Promise<ProfessionalWithMutualCount[]> {
    const r = await apiClient.get(url('/api/networking/people_you_may_know/'));
    return r.data;
}

/** Paginated variant of PYMK used by the Discover tab.
 *  Returns the raw DRF paged shape so `usePagedList` can consume it
 *  directly. Consumer-side: each result has a `mutual_count` field. */
export async function listPeopleYouMayKnow(
    page: number = 1,
): Promise<PagedResponse<ProfessionalWithMutualCount>> {
    const r = await apiClient.get(
        url(`/api/networking/people_you_may_know/?page=${page}`),
    );
    // Server returns { results, count, next } — the `next` field is a
    // boolean here (per backend `people_you_may_know_view`), but we
    // normalise to PagedResponse<T>.next = string | null for the hook.
    const body = r.data;
    if (Array.isArray(body)) {
        return { results: body, next: null, previous: null, count: body.length };
    }
    return {
        results: body.results ?? [],
        next: body.next ? `/api/networking/people_you_may_know/?page=${page + 1}` : null,
        previous: page > 1 ? `/api/networking/people_you_may_know/?page=${page - 1}` : null,
        count: body.count ?? 0,
    };
}

// ────────────────────────────────────────────────────────────────────────
// Connections
// ────────────────────────────────────────────────────────────────────────

export async function listConnections(
    page: number = 1,
): Promise<PagedResponse<Professional>> {
    const r = await apiClient.get(url(`/api/networking/connections/?page=${page}`));
    return r.data;
}

export type RequestsPage = {
    received: PagedResponse<Connection>;
    sent: PagedResponse<Connection>;
};

export async function listRequests(
    pages: { received?: number; sent?: number } = {},
): Promise<RequestsPage> {
    const params = new URLSearchParams();
    if (pages.received) params.set('received_page', String(pages.received));
    if (pages.sent) params.set('sent_page', String(pages.sent));
    const qs = params.toString();
    const r = await apiClient.get(
        url(`/api/networking/connections/requests/${qs ? '?' + qs : ''}`),
    );
    return r.data;
}

export async function sendConnectionRequest(toUserId: number): Promise<{ detail: string }> {
    const r = await apiClient.post(
        url('/api/networking/connections/request/'),
        { to_user_id: toUserId },
    );
    return r.data;
}

export async function acceptConnectionRequest(connectionId: string): Promise<{ detail: string }> {
    const r = await apiClient.post(url(`/api/networking/connections/${connectionId}/accept/`));
    return r.data;
}

export async function declineConnectionRequest(connectionId: string): Promise<{ detail: string }> {
    const r = await apiClient.post(url(`/api/networking/connections/${connectionId}/decline/`));
    return r.data;
}

export async function cancelConnectionRequest(connectionId: string): Promise<{ detail: string }> {
    const r = await apiClient.post(url(`/api/networking/connections/${connectionId}/cancel/`));
    return r.data;
}

export async function removeConnection(connectionId: string): Promise<{ detail: string }> {
    const r = await apiClient.delete(url(`/api/networking/connections/${connectionId}/`));
    return r.data;
}

// ────────────────────────────────────────────────────────────────────────
// Inbox + read receipts
// ────────────────────────────────────────────────────────────────────────

export async function listConversations(
    page: number = 1,
): Promise<PagedResponse<ConversationSummary>> {
    const r = await apiClient.get(
        url(`/api/networking/conversations/?page=${page}`),
    );
    return r.data;
}

export async function markDmThreadRead(peerId: number): Promise<{ marked: number }> {
    const r = await apiClient.post(url(`/api/networking/messages/${peerId}/read_all/`));
    return r.data;
}

export async function markGroupRead(groupId: string): Promise<{ detail: string }> {
    const r = await apiClient.post(url(`/api/networking/groups/${groupId}/read_all/`));
    return r.data;
}

// ────────────────────────────────────────────────────────────────────────
// Direct messages
// ────────────────────────────────────────────────────────────────────────

export async function listDirectMessages(
    peerId: number,
    page: number = 1,
): Promise<PagedResponse<DirectMessage>> {
    const r = await apiClient.get(url(`/api/networking/messages/${peerId}/?page=${page}`));
    return r.data;
}

export async function postDirectMessage(
    peerId: number,
    content: string,
    clientMessageId?: string,
    file?: File | null,
): Promise<DirectMessage> {
    // `Idempotency-Key` is honoured server-side by `api.idempotency.@idempotent`
    // — a flaky-network retry with the same clientMessageId replays the
    // cached first response instead of creating a duplicate row.
    const headers: Record<string, string> = {};
    if (clientMessageId) headers['Idempotency-Key'] = clientMessageId;

    // When a file is attached, send as multipart/form-data so the backend's
    // `request.FILES` handler receives it alongside the text content.
    if (file) {
        const fd = new FormData();
        fd.append('content', content);
        fd.append('client_message_id', clientMessageId || '');
        fd.append('file', file);
        const r = await apiClient.post(
            url(`/api/networking/messages/${peerId}/`),
            fd,
            { headers },
        );
        return r.data;
    }
    const r = await apiClient.post(
        url(`/api/networking/messages/${peerId}/`),
        { content, client_message_id: clientMessageId || '' },
        { headers },
    );
    return r.data;
}

export async function editDirectMessage(
    messageId: string,
    content: string,
): Promise<DirectMessage> {
    const r = await apiClient.patch(
        url(`/api/networking/messages/detail/${messageId}/`),
        { content },
    );
    return r.data;
}

export async function deleteDirectMessage(messageId: string): Promise<void> {
    await apiClient.delete(url(`/api/networking/messages/detail/${messageId}/`));
}

// ────────────────────────────────────────────────────────────────────────
// Groups
// ────────────────────────────────────────────────────────────────────────

export async function listGroups(page: number = 1): Promise<PagedResponse<GroupChat>> {
    const r = await apiClient.get(url(`/api/networking/groups/?page=${page}`));
    return r.data;
}

/** Fetch a single group by id. Used by GroupSettingsModal to hydrate
 *  description + avatar that ConversationSummary doesn't carry. */
export async function getGroup(groupId: string): Promise<GroupChat> {
    const r = await apiClient.get(url(`/api/networking/groups/${groupId}/`));
    return r.data;
}

export async function createGroup(
    name: string,
    memberIds: number[],
): Promise<GroupChat> {
    const r = await apiClient.post(url('/api/networking/groups/create/'), {
        name,
        member_ids: memberIds,
    });
    return r.data;
}

export async function updateGroup(
    groupId: string,
    fields: Partial<Pick<GroupChat, 'name' | 'description' | 'avatar'>>,
): Promise<GroupChat> {
    const r = await apiClient.patch(url(`/api/networking/groups/${groupId}/`), fields);
    return r.data;
}

export async function deleteGroup(groupId: string): Promise<void> {
    await apiClient.delete(url(`/api/networking/groups/${groupId}/`));
}

export async function listGroupMessages(
    groupId: string,
    page: number = 1,
): Promise<PagedResponse<GroupMessage>> {
    const r = await apiClient.get(url(`/api/networking/groups/${groupId}/messages/?page=${page}`));
    return r.data;
}

export async function postGroupMessage(
    groupId: string,
    content: string,
    file?: File | null,
    clientMessageId?: string,
): Promise<GroupMessage> {
    const headers: Record<string, string> = {};
    if (clientMessageId) headers['Idempotency-Key'] = clientMessageId;

    if (file) {
        const fd = new FormData();
        fd.append('content', content);
        fd.append('file', file);
        const r = await apiClient.post(
            url(`/api/networking/groups/${groupId}/messages/`),
            fd,
            { headers },
        );
        return r.data;
    }
    const r = await apiClient.post(
        url(`/api/networking/groups/${groupId}/messages/`),
        { content },
        { headers },
    );
    return r.data;
}

export async function editGroupMessage(
    messageId: string,
    content: string,
): Promise<GroupMessage> {
    const r = await apiClient.patch(
        url(`/api/networking/groups/messages/${messageId}/`),
        { content },
    );
    return r.data;
}

export async function deleteGroupMessage(messageId: string): Promise<void> {
    await apiClient.delete(url(`/api/networking/groups/messages/${messageId}/`));
}

export async function listGroupMembers(groupId: string): Promise<GroupMembership[]> {
    const r = await apiClient.get(url(`/api/networking/groups/${groupId}/members/`));
    return r.data;
}

export async function addGroupMember(
    groupId: string,
    userId: number,
): Promise<GroupMembership> {
    const r = await apiClient.post(
        url(`/api/networking/groups/${groupId}/members/add/`),
        { user_id: userId },
    );
    return r.data;
}

export async function removeGroupMember(
    groupId: string,
    userId: number,
): Promise<void> {
    await apiClient.delete(url(`/api/networking/groups/${groupId}/members/${userId}/`));
}

export async function leaveGroup(groupId: string): Promise<{ detail: string }> {
    const r = await apiClient.post(url(`/api/networking/groups/${groupId}/leave/`));
    return r.data;
}

export async function transferGroupOwnership(
    groupId: string,
    newOwnerId: number,
): Promise<{ detail: string }> {
    const r = await apiClient.post(
        url(`/api/networking/groups/${groupId}/transfer_ownership/`),
        { new_owner_id: newOwnerId },
    );
    return r.data;
}

// ────────────────────────────────────────────────────────────────────────
// Block / report / safety
// ────────────────────────────────────────────────────────────────────────

export async function blockUser(userId: number, reason: string = ''): Promise<Block> {
    const r = await apiClient.post(
        url(`/api/networking/users/${userId}/block/`),
        { reason },
    );
    return r.data;
}

export async function unblockUser(userId: number): Promise<void> {
    await apiClient.delete(url(`/api/networking/users/${userId}/unblock/`));
}

export async function listBlocks(page: number = 1): Promise<PagedResponse<Block>> {
    const r = await apiClient.get(url(`/api/networking/blocks/?page=${page}`));
    return r.data;
}

export async function reportUser(
    userId: number,
    category: AbuseCategory,
    details: string,
): Promise<AbuseReport> {
    const r = await apiClient.post(
        url(`/api/networking/users/${userId}/report/`),
        { category, details },
    );
    return r.data;
}

// ────────────────────────────────────────────────────────────────────────
// Admin moderation
// ────────────────────────────────────────────────────────────────────────

export async function listAbuseReports(
    state?: AbuseState,
): Promise<PagedResponse<AbuseReport>> {
    const qs = state ? `?state=${state}` : '';
    const r = await apiClient.get(url(`/api/networking/admin/abuse_reports/${qs}`));
    return r.data;
}

export async function actionAbuseReport(
    reportId: string,
    action: 'actioned' | 'dismissed',
): Promise<AbuseReport> {
    const r = await apiClient.post(
        url(`/api/networking/admin/abuse_reports/${reportId}/action/`),
        { action },
    );
    return r.data;
}
